<template>
  <v-card :flat="flat" class="customer-cart-detail">
    <!-- Header principal mejorado -->
    <v-card-title class="pb-2">
      <v-icon left color="primary" size="28">mdi-cart</v-icon>
      <span class="text-h5 font-weight-bold primary--text">
        {{ $t('delivery.myOrder') }}
      </span>
      <v-spacer></v-spacer>
      <v-chip
          v-if="getTotalQuantity > 0"
          color="primary"
          text-color="white"
          class="font-weight-bold"
      >
        <v-icon left small>mdi-package-variant</v-icon>
        {{ getTotalQuantity }} items
      </v-chip>
    </v-card-title>

    <v-divider class="mb-4"></v-divider>

    <!-- Estado vacío mejorado -->
    <v-card-text v-if="getTotalQuantity === 0" class="text-center py-8">
      <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-cart-outline</v-icon>
      <div class="text-h6 grey--text text--darken-2 mb-2">
        {{ $t('delivery.emptyOrder') }}
      </div>
      <div class="text-body-2 grey--text">
        Agrega productos para comenzar tu pedido
      </div>
    </v-card-text>

    <v-card-text v-else class="pa-2">
      <!-- Sección de Promos mejorada -->
      <template v-if="getPromos.length > 0">
        <div class="mb-4">
          <div class="d-flex align-center mb-3">
            <v-icon color="orange" class="mr-2">mdi-tag-multiple</v-icon>
            <h6 class="text-h6 font-weight-bold orange--text text--darken-2">
              Promociones Aplicadas
            </h6>
          </div>

          <v-card outlined class="mb-3">
            <v-list class="py-0" dense>
              <div v-for="(orderPromo, index) in getPromos" :key="orderPromo.id">
                <customer-cart-detail-promo :order-promo="orderPromo"></customer-cart-detail-promo>
                <v-divider v-if="index < getPromos.length - 1" class="my-1"></v-divider>
              </div>
            </v-list>
          </v-card>
        </div>
      </template>

      <!-- Sección de Productos por Categoría mejorada -->
      <template v-for="category in getCategories">
        <div v-if="hasCategoryItems(category)" class="mb-4" :key="category.id">
          <v-card elevation="2" class="category-card">
            <!-- Header de categoría -->
            <v-card-title
                class="py-3"
                :style="{
                backgroundColor: category.backgroundColor || '#1976d2',
                color: category.fontColor || 'white'
              }"
            >
              <v-icon left :color="category.fontColor || 'white'" class="mr-2">
                mdi-tag
              </v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{ category.name }}</span>


            </v-card-title>

            <!-- Items de la categoría -->
            <v-card-text class="pa-0">
              <v-list class="py-0" dense>
                <div v-for="(item, index) in getItemsByCategory(category)"
                     :class="{ 'grey lighten-5': index % 2 === 0 }"
                     :key="item.product.id"
                >
                  <customer-cart-detail-product
                      :category="category"
                      :item="item"
                      class="px-3 py-2"
                  ></customer-cart-detail-product>
                </div>
              </v-list>
            </v-card-text>

            <!-- Footer con subtotal de categoría -->
            <v-card-actions
            >
              <v-spacer></v-spacer>
              <div class="text-right">
                <div class="text-body-2 grey--text text--darken-2 mb-1">
                  <v-chip
                      outlined
                      :color="category.backgroundColor || 'black'"
                      :text-color="category.fontColor || 'black'"
                      class="font-weight-bold"
                  >
                    {{ categoryItemsQuantity(category) }} Total
                  </v-chip>

                </div>

              </div>
            </v-card-actions>
          </v-card>
        </div>
      </template>


      <!-- Sección de totales mejorada -->
      <template v-if="getTotalQuantity > 0">
        <v-divider class="my-3"></v-divider>
        <cart-total
            :items-amount="promosAndItemsAmount"
            :delivery-cost="deliveryCost"
            :delivery-free="deliveryFree"
            :discount-rate="discountRate"
            :totalDiscount="totalDiscount"
            :total-amount="totalAmount"
            :total-amount-with-discount="totalAmountWithDiscount"
        />

        <!-- Sección de cupón -->
        <v-card v-if="couponEnable" class="mt-3 elevation-2">
          <coupon-invalid></coupon-invalid>
          <template v-if="couponCode && (discountRate || discountAmount)">
            <v-card-text>
              <coupon-view :order="getOrder"></coupon-view>

              <v-btn
                  @click="removeCoupon"
                  color="red"
                  block outlined
                  :disabled="!couponCode"
                  small
              >
                <v-icon left small>mdi-delete</v-icon>
                Remover cupón
              </v-btn>
            </v-card-text>
          </template>

          <template v-else>
            <v-card-title>
              <v-icon left color="teal">mdi-ticket-percent</v-icon>
              <span class="text-subtitle-1 font-weight-bold teal--text">
            Cupón de Descuento
          </span>
            </v-card-title>
            <v-card-text>
              <v-row dense align="center">
                <v-col cols="12">
                  <v-text-field
                      label="Código del cupón"
                      outlined
                      dense
                      hide-details
                      v-model="couponCode"
                      prepend-inner-icon="mdi-ticket-percent"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-btn
                      @click="applyCoupon"
                      color="teal"
                      class="white--text font-weight-bold"
                      block
                      :disabled="!couponCode"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    APLICAR
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </template>
        </v-card>

      </template>
    </v-card-text>
    <!-- Alertas de cantidad mínima mejoradas -->
    <v-card-text v-if="!isMinimumQuantityPerCategoryReached && getTotalQuantity > 0">
      <div class="mb-2">
        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
        <span class="text-subtitle-2 font-weight-bold warning--text text--darken-2">
          Cantidad Mínima Requerida
        </span>
      </div>
      <v-alert
          v-for="category in getCategoriesBelowMinimunQuantity"
          :key="category.id"
          type="warning"
          outlined
          dense
          class="mb-2"
      >
        <div class="d-flex align-center">
          <v-icon left color="warning">mdi-information</v-icon>
          <span>
            El mínimo para {{ isDelivery ? 'delivery' : 'take away' }} son
            <strong>{{ getCategoryMinimunQuantity(category) }} {{ category.name }}</strong>
          </span>
        </div>
      </v-alert>
    </v-card-text>

    <!-- Acciones del carrito -->
    <template v-if="showActions">
      <v-divider></v-divider>
      <v-card-actions class="pa-3">
        <customer-cart-detail-actions
            :is-cart-ready="isCartReady"
            :quantity-total="getTotalQuantity"
            @next="next"
            @close="close"
        />
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>

import CartTotal from "@/modules/delivery/components-core/shared/CartTotal/CartTotal";
import ProductMixin from "@/modules/delivery/mixins/ProductMixin";
import CustomerCartDetailProduct
  from "@/modules/delivery/components-core/customer/CustomerCartDetail/CustomerCartDetailProduct.vue";
import CustomerCartDetailPromo
  from "@/modules/delivery/components-core/customer/CustomerCartDetail/CustomerCartDetailPromo.vue";
import CustomerCartDetailActions
  from "@/modules/delivery/components-core/customer/CustomerCartDetail/CustomerCartDetailActions.vue";
import CouponView from "@/modules/delivery/components/CouponView/CouponView.vue";
import CouponInvalid from "@/modules/delivery/components/CouponInvalid/CouponInvalid.vue";

export default {
  name: "CustomerCartDetail",
  components: {
    CouponInvalid,
    CouponView, CustomerCartDetailActions, CustomerCartDetailProduct, CustomerCartDetailPromo, CartTotal},
  mixins: [ProductMixin],
  props: {
    flat: {type: Boolean, default: false},
    items: {type: Array},
    showActions: {type: Boolean, default: false},
  },
  computed: {
    couponCode: {
      get() {
        return this.$store.getters.orderSystem.order.couponCode
      },
      set(val) {
        this.$store.commit('couponCode', val)
      }
    },
    getCategoryMinimunQuantity() {
      return category => this.$store.getters.orderSystem.cartService.getCategoryMinimunQuantity(category.id)
    },
    getOrder() {
      return this.$store.getters.orderSystem.order
    },
    getItems() {
      return this.$store.getters.orderSystem.order.items
    },
    getTotalQuantity() {
      return this.$store.getters.orderSystem.getTotalQuantity
    },
    getPromos() {
      return this.$store.getters.orderSystem.order.promos
    },
    deliveryCost() {
      return this.$store.getters.orderSystem.order.delivery.cost
    },
    deliveryFree() {
      return this.$store.getters.orderSystem.order.deliveryFree
    },
    productsAmount() {
      return this.$store.getters.orderSystem.cartService.getItemsAmount
    },
    promosAndItemsAmount() {
      return this.$store.getters.orderSystem.cartService.getPromosAndItemsAmount
    },
    discountRate() {
      return this.$store.getters.orderSystem.order.discountRate
    },
    discountAmount() {
      return this.$store.getters.orderSystem.order.discountAmount
    },
    totalDiscount() {
      return this.$store.getters.orderSystem.cartService.getTotalDiscount
    },

    totalAmount() {
      return this.$store.getters.orderSystem.cartService.getTotalAmount
    },
    totalAmountWithDiscount() {
      return this.$store.getters.orderSystem.cartService.getTotalAmountWithDiscount
    },
    isDelivery() {
      return this.$store.getters.orderSystem.order.isDelivery
    },
    isCartReady() {
      return this.$store.getters.orderSystem.isCartReady
    },
    isMinimumQuantityPerCategoryReached() {
      return this.$store.getters.orderSystem.cartService.isMinimumQuantityPerCategoryReached
    },
    getCategoriesBelowMinimunQuantity() {
      return this.$store.getters.orderSystem.cartService.getCategoriesBelowMinimunQuantity
    },

    getCategories() {
      return this.$store.getters.categories
    },
    getItemsByCategory() {
      return category => {
        return this.$store.getters.orderSystem.cartService.getItemsByCategory(category)
      }
    },
    hasCategoryItems() {
      return category => {
        return this.$store.getters.orderSystem.cartService.hasCategoryItems(category)
      }
    },
    categoryItemsQuantity() {
      return category => {
        return this.$store.getters.orderSystem.cartService.getQuantityByCategory(category)
      }
    },
    couponEnable(){
      return this.$store.getters.getSettingValue('couponEnable')
    }

  },
  methods: {
    applyCoupon() {
      this.$store.dispatch('findCouponByCode', this.couponCode)
    },
    removeCoupon() {
      this.$store.commit('clearCoupon')
    },
    next() {
      this.$emit('next')
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.customer-cart-detail {
  transition: all 0.3s ease;
}

.category-card {
  transition: all 0.3s ease;
  margin-bottom: 16px;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.v-list-item {
  transition: background-color 0.2s ease;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.v-card {
  border-radius: 8px !important;
}

.v-chip {
  border-radius: 6px !important;
}
</style>
